import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';

type SetNode = (node: ReactNode) => void;

// Split into two contexts rather than one `{ node, setNode }` object: a
// component only ever needs ONE side of this (AppHeader reads `node`,
// PageHeader only calls `setNode`), and bundling them together made every
// *publisher* also a subscriber of the *value* it was writing. `node` is a
// fresh element every render (see `usePageTitleSlot` below), so each publish
// forced `PageHeader` itself to re-render, producing a new `node` reference,
// re-firing the effect, publishing again — an infinite loop that pegged the
// CPU on every page (invisible in a screenshot: the output looks identical
// each pass) and, once nothing masked it, hung a test to an OOM crash.
// `setNode` from `useState` is referentially stable for the life of the
// component, so a context carrying ONLY `setNode` never changes — publishers
// subscribe to that and are never forced to re-render by their own writes.
const NO_PROVIDER = Symbol('page-title-slot-no-provider');
const PageTitleSlotValueContext = createContext<ReactNode | typeof NO_PROVIDER>(NO_PROVIDER);
const PageTitleSlotSetterContext = createContext<SetNode | null>(null);

/**
 * Lets a page inject content into the fixed `AppHeader`, opposite the brand
 * mark. `AppHeader` is mounted once in `AppLayout`, outside every route's own
 * component tree, so a page can't just render into it directly — this is the
 * one shared slot it publishes into instead.
 *
 * `PageHeader` calls `usePageTitleSlot` internally, so every page that
 * renders a `<PageHeader title=.../>` gets this for free — the title/
 * subtitle move to the fixed header automatically. Call the hook directly
 * only for a page with a title but no `PageHeader` (rare; check first).
 */
export function PageTitleSlotProvider({ children }: { children: ReactNode }) {
  const [node, setNode] = useState<ReactNode>(null);
  return (
    <PageTitleSlotSetterContext.Provider value={setNode}>
      <PageTitleSlotValueContext.Provider value={node}>
        {children}
      </PageTitleSlotValueContext.Provider>
    </PageTitleSlotSetterContext.Provider>
  );
}

/** Read the current slot content. Used by `AppHeader` only. */
export function usePageTitleSlotValue(): ReactNode {
  const value = useContext(PageTitleSlotValueContext);
  if (value === NO_PROVIDER) {
    throw new Error('usePageTitleSlotValue must be used within PageTitleSlotProvider');
  }
  return value;
}

/**
 * Publish `node` into the header's title slot for as long as the calling
 * component stays mounted; clears itself on unmount (or route change) so
 * navigating away never leaves a stale title behind.
 */
export function usePageTitleSlot(node: ReactNode): void {
  const setNode = useContext(PageTitleSlotSetterContext);
  if (!setNode) throw new Error('usePageTitleSlot must be used within PageTitleSlotProvider');
  useEffect(() => {
    setNode(node);
    return () => setNode(null);
  }, [node, setNode]);
}
